import {TimelineContextMenu} from "../timelineContextMenu";
import {CombinedTimelineDayComponentConfig, WorkingTimeTimelineDayContent} from "../timelineTypes";
import {hasWorkingTimeTypesToAdd} from "../timelineHelper";
import {minutesToMillis} from "@/shared/utils/misc";
import {
	formatDate,
	formatDateDay,
	formatDateWithoutYear,
	formatDuration,
	parseISODate,
	Temporal
} from "@/shared/utils/datefunctions";
import {WorkingTime} from "@/shared/timr";
import {TimelineMode} from "@/shared/timrTypes";

/**
 * Component to display the title of a timeline day including date and statistics.
 * This component is used inside <code>TimelineDay</code> to render the title for a specific day. If available this component is appended to the day container.
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class CombinedTimelineDayTitle extends HTMLElement {
	private _timelineDay: WorkingTimeTimelineDayContent;
	private _config: CombinedTimelineDayComponentConfig;
	private _contextMenuForDate: (date: Temporal.PlainDate | null) => TimelineContextMenu<WorkingTime>;

	set timelineDay(value: WorkingTimeTimelineDayContent) {
		this._timelineDay = value;
	}

	set config(value: CombinedTimelineDayComponentConfig) {
		this._config = value;
	}

	set contextMenuForDate(value: (date: Temporal.PlainDate | null) => TimelineContextMenu<WorkingTime>) {
		this._contextMenuForDate = value;
	}

	/**
	 * Main entry point to render the title including date and statistics.
	 */
	public connectedCallback(): void {
		// the content (timelineDay), config and contextMenu are required to render the title
		if (!this._timelineDay || !this._config || !this._contextMenuForDate) {
			return;
		}

		const {statistics, date} = this._timelineDay;
		const parsedDate = parseISODate(date);
		const dateSpan = this._config.mode === TimelineMode.Daily ?
			`<span>${formatDateDay(parsedDate)}, ${formatDate(parsedDate)}</span>` :
			`<span>${formatDateDay(parsedDate)}<br/> ${formatDateWithoutYear(parsedDate)}</span>`;

		this.innerHTML = `
				${dateSpan}
				<div style="display: flex">
					<div class="title-statistics">
						<span><i class="fal fa-coffee position-left"></i>${formatDuration(statistics.gapsDurationInMilliseconds + minutesToMillis(statistics.breakTimesDurationInMinutes))}</span>
						<span><i class="fal fa-user-clock position-left"></i>${formatDuration(statistics.workingTimesDurationInMilliseconds)}</span>
					</div>
					<div class="dropdown" ${hasWorkingTimeTypesToAdd(this._config) ? "" : "disabled"} data-context-menu-container>
						<button class="btn-link btn-link-primary pt-0 pb-0 m-0" aria-expanded="false" data-context-menu-button>
							<i class="fal fa-plus"></i>
						</button>
					</div>
				</div>
			`;

		if (globals.projectTimeEnabled) {
			const span = document.createElement("span");
			span.innerHTML = `<i class="fal fa-business-time position-left"></i>${formatDuration(this._timelineDay.statistics.projectTimesDurationInMilliseconds)}`;
			this.querySelector(".title-statistics").append(span);
		}

		this._contextMenuForDate(parsedDate).bindToElement(this);
	}
}
